import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Animated, TextInput, TextInputContentSizeChangeEvent, View } from 'react-native'
import { useTheme } from '@ui-kitten/components'
import { StyleService, useStyleSheet } from '@style/service'
import { IconButton } from '@components/base'
import { AttachmentAddButton } from '@components/AttachmentAddButton'
import { GradientBorder } from '@components/GradientBorder'
import { MicrophoneButton } from '@components/MicrophoneButton'
import { Feature, LoadingIndicator, useFeatureFlag } from '@components'
import { useVoice } from '@utils/voice'
import { useNoraStreamChat } from '@screens/Nora/context/NoraStreamChatContext.tsx'
import { useNoraFileAttachments } from '@screens/Nora/context/NoraFileAttachmentsContext'
import { Analytics, CustomEventTypes } from '@config'
import { usePlaceholderRotation } from '@screens/Nora/hooks/usePlaceholderRotation'
import { useIsDarkMode } from '@config/theme.ts'
import { AttachmentsPreviewWithAddMore } from './AttachmentsPreviewWithAddMore'

const GRADIENT_BORDER_WIDTH = 2
const GRADIENT_BORDER_RADIUS = 16
const INPUT_LINE_HEIGHT = 22
const MAX_INPUT_LINES = 5

export const AskNoraInput = ({ onType }: { onType?: () => void }) => {
  const styles = useStyleSheet(themedStyle)
  const theme = useTheme()
  const isDarkMode = useIsDarkMode()
  const chatbotFileAttachmentsEnabled = useFeatureFlag(Feature.ChatbotFileAttachments)
  const [question, setQuestion] = useState('')
  const [isAsking, setIsAsking] = useState(false)
  const [numberOfLines, setNumberOfLines] = useState(1)

  const handleContentSizeChange = useCallback((e: TextInputContentSizeChangeEvent) => {
    const lines = Math.max(
      1,
      Math.min(MAX_INPUT_LINES, Math.ceil(e.nativeEvent.contentSize.height / INPUT_LINE_HEIGHT)),
    )
    setNumberOfLines(lines)
  }, [])

  const { text: placeholder, opacity: placeholderOpacity } = usePlaceholderRotation()
  const { onAsk, ensureAiProcessingConsent } = useNoraStreamChat()

  const inputRef = useRef<TextInput>(null)
  const questionRef = useRef(question)
  questionRef.current = question

  const handleTranscript = useCallback((newText: string) => {
    const currentQuestion = questionRef.current
    const newQuestion = currentQuestion ? `${currentQuestion} ${newText}` : newText
    setQuestion(newQuestion)
  }, [])

  const { isAvailable, isListening, startListening, stopListening } = useVoice(handleTranscript)

  const noraFileAttachments = useNoraFileAttachments()
  const hasAttachmentSupport = chatbotFileAttachmentsEnabled && !!noraFileAttachments
  const attachments = hasAttachmentSupport ? noraFileAttachments.attachments : []
  const hasAttachments = attachments.length > 0
  const hasUploadingAttachments = hasAttachmentSupport ? noraFileAttachments.isUploading : false
  const attachmentLimitReached = hasAttachmentSupport ? !noraFileAttachments.canAttachMore : false
  const isAttachmentActionDisabled = attachmentLimitReached || hasUploadingAttachments || isAsking

  useEffect(() => {
    if (!chatbotFileAttachmentsEnabled && noraFileAttachments?.attachments.length) {
      noraFileAttachments.clearAttachments()
    }
  }, [chatbotFileAttachmentsEnabled, noraFileAttachments])
  const handleToggleMicrophone = useCallback(async () => {
    if (isListening) {
      await stopListening()
      inputRef.current?.focus()
    } else {
      await startListening()
    }
  }, [isListening, startListening, stopListening])

  const handleAsk = async (prompt: string) => {
    const trimmedPrompt = prompt.trim()
    const hasPrompt = trimmedPrompt.length > 0

    if (!hasPrompt) {
      return
    }

    if (isListening) {
      await stopListening()
    }

    let userFileIds: string[] = []
    if (hasAttachmentSupport && hasAttachments) {
      // Files must not leave the device before AI processing consent (onAsk checks too late for uploads)
      if (!(await ensureAiProcessingConsent())) {
        return
      }

      try {
        userFileIds = await noraFileAttachments.uploadAttachmentsAndGetUserFileIds()
      } catch {
        return
      }
    }
    const userFilePreviews =
      hasAttachmentSupport && userFileIds.length > 0
        ? noraFileAttachments.getUploadedAttachmentPreviews()
        : undefined

    setIsAsking(true)
    const sent = await onAsk({
      prompt: trimmedPrompt,
      userFileIds: userFileIds.length ? userFileIds : undefined,
      userFilePreviews,
    })
    setIsAsking(false)

    // Keep the draft when nothing was sent (e.g. AI consent declined or a server error)
    if (!sent) {
      return
    }

    setQuestion('')
    setNumberOfLines(1)
    if (hasAttachmentSupport) {
      noraFileAttachments.clearAttachments()
    }
    Analytics.track(CustomEventTypes.NoraHomeMessageSent)
  }

  const microphoneButton = isAvailable && (
    <MicrophoneButton isListening={isListening} onPress={handleToggleMicrophone} />
  )

  const hasMessageContent = question.trim().length > 0
  const isSendEnabled = !isAsking && !isListening && !hasUploadingAttachments && hasMessageContent

  const sendButton = (
    <View style={styles.sendButtonWrapper}>
      <IconButton
        accessibilityLabel="Send message"
        iconName="paper-plane-tilt"
        size="s"
        type="transparent"
        onPress={() => handleAsk(question)}
        iconProps={{ style: styles.sendIcon }}
      />
    </View>
  )

  const getAccessoryRight = () => {
    if (isAsking || hasUploadingAttachments) {
      return (
        <LoadingIndicator
          size="small"
          viewStyles={styles.loadingIndicator}
          color={theme['theme.primary.base']}
        />
      )
    }
    if (isSendEnabled) {
      return sendButton
    }
    return microphoneButton || undefined
  }

  const accessoryRight = getAccessoryRight()
  const showAnimatedPlaceholder = !question

  return (
    <View style={styles.inputWrapper}>
      <View style={styles.inputFieldWrapper}>
        <GradientBorder borderWidth={GRADIENT_BORDER_WIDTH} borderRadius={GRADIENT_BORDER_RADIUS}>
          <View style={[styles.inputContainer, isDarkMode && styles.inputContainerDark]}>
            {hasAttachmentSupport && hasAttachments && (
              <AttachmentsPreviewWithAddMore isAsking={isAsking} />
            )}
            <View
              style={[
                styles.textRow,
                hasAttachmentSupport && hasAttachments && styles.textRowWithAttachments,
              ]}
            >
              {hasAttachmentSupport && !hasAttachments && (
                <AttachmentAddButton
                  disabled={isAttachmentActionDisabled}
                  onPress={noraFileAttachments.openAttachmentPicker}
                />
              )}

              <View style={styles.textInputWrapper}>
                <TextInput
                  ref={inputRef}
                  autoCapitalize="sentences"
                  multiline
                  numberOfLines={numberOfLines}
                  onContentSizeChange={handleContentSizeChange}
                  returnKeyType="send"
                  spellCheck={false}
                  value={question}
                  onSubmitEditing={() => handleAsk(question)}
                  editable={!isAsking}
                  placeholder={showAnimatedPlaceholder ? '' : placeholder}
                  placeholderTextColor={theme['theme.text.tertiary']}
                  style={styles.textInput}
                  onChangeText={(text) => {
                    setQuestion(text)
                    onType?.()
                  }}
                />
                {showAnimatedPlaceholder && (
                  <View style={styles.animatedPlaceholderContainer} pointerEvents="none">
                    <Animated.Text
                      style={[styles.animatedPlaceholderText, { opacity: placeholderOpacity }]}
                      numberOfLines={1}
                    >
                      {placeholder}
                    </Animated.Text>
                  </View>
                )}
              </View>

              <View style={styles.rightAccessoryContainer}>
                {accessoryRight || <View style={styles.rightAccessorySpacer} />}
              </View>
            </View>
          </View>
        </GradientBorder>
      </View>
    </View>
  )
}

const themedStyle = StyleService.create({
  inputWrapper: {
    width: '100%',
  },
  inputContainer: {
    borderRadius: GRADIENT_BORDER_RADIUS - GRADIENT_BORDER_WIDTH,
    backgroundColor: 'theme.surface.base3',
    padding: 12,
    gap: 8,
  },
  inputContainerDark: {
    backgroundColor: 'theme.surface.base1',
  },
  sendButtonWrapper: {
    backgroundColor: 'theme.text.secondary',
    borderRadius: 16,
  },
  sendIcon: {
    color: 'theme.solid.white',
  },
  loadingIndicator: {
    justifyContent: 'center',
    alignItems: 'center',
    width: 32,
    height: 32,
  },
  inputFieldWrapper: {
    width: '100%',
    position: 'relative',
  },
  textRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    width: '100%',
    gap: 8,
  },
  textRowWithAttachments: {
    alignItems: 'center',
  },
  textInputWrapper: {
    flex: 1,
    minHeight: 32,
    justifyContent: 'center',
    position: 'relative',
  },
  textInput: {
    fontSize: 17,
    lineHeight: INPUT_LINE_HEIGHT,
    color: 'theme.text.primary',
    maxHeight: INPUT_LINE_HEIGHT * MAX_INPUT_LINES,
    padding: 0,
  },
  animatedPlaceholderContainer: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    justifyContent: 'center',
  },
  animatedPlaceholderText: {
    fontSize: 17,
    color: 'theme.text.tertiary',
  },
  rightAccessoryContainer: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rightAccessorySpacer: {
    width: 32,
    height: 32,
  },
})
